import axios from 'axios';
import { create } from 'zustand';

import {
  mapSiteNavigation,
  type SiteNavigationModel,
} from '@/components/layout/site-header/site-navigation-mappers';
import { logger } from '@/lib/logger';
import { SiteNavigationService } from '@/services/site-navigation.service';

const siteNavigationService = new SiteNavigationService();

export type SiteNavigationStatus = 'idle' | 'loading' | 'ready' | 'error';

type SiteNavigationState = {
  status: SiteNavigationStatus;
  /** Set only when `status === 'ready'`. */
  navigation: SiteNavigationModel | null;
  /** Reads the published navigation once per page load; concurrent callers share one request. */
  load: () => Promise<void>;
};

// Shared in-flight promise so every layout, hook and the drawer collapse onto ONE request.
let inFlight: Promise<void> | null = null;

/**
 * Surfaces items the mapper had to drop from a PUBLISHED navigation.
 *
 * ## Invariants:
 * - A publish with items that leaves NONE usable is a broken publish: 'siteNavigation.noUsableNodes'
 *   goes to Rollbar (`logger.error`). The header still renders (logo only), as the api asked.
 * - Some items dropped is only a warning: a menu item whose page was unpublished is dropped by
 *   design, and an error per page load for it would flood Rollbar.
 */
function reportDroppedNodes(navigation: SiteNavigationModel): void {
  if (navigation.source !== 'PUBLISHED' || navigation.droppedNodes === 0) return;
  const context = {
    feature: 'site-navigation',
    route: 'map',
    droppedNodes: navigation.droppedNodes,
  };
  if (navigation.nodes.length === 0) {
    logger.error('siteNavigation.noUsableNodes', undefined, context);
  } else {
    logger.warn('siteNavigation.nodesDropped', context);
  }
}

/**
 * The published site navigation, shared by both layouts.
 *
 * ## Invariants:
 * - ONE request per page load, never a per-user one: the response is public and domain-resolved,
 *   and audience / session filtering happen on the client. `load()` is a no-op once the status
 *   is anything but `idle`, and an in-flight read is shared.
 * - A failed read (rejected promise, timeout, non-2xx including a 404 from a pod that predates the
 *   endpoint, or a payload that is not a navigation) ends as `error` AFTER a
 *   `logger.error('siteNavigation.loadFailed', …)`. Consumers render today's header for `error`.
 *   It is never retried within the page view and never turned into a quiet DEFAULT.
 * - The caller decides whether to load at all (`theme.siteBuilderEnabled`); this store never reads
 *   the theme, so a flag-off company costs no request.
 */
export const useSiteNavigationStore = create<SiteNavigationState>((set, get) => ({
  status: 'idle',
  navigation: null,
  load: () => {
    if (inFlight && get().status === 'loading') return inFlight;
    if (get().status !== 'idle') return Promise.resolve();
    set({ status: 'loading' });
    const request = (async () => {
      try {
        const payload = await siteNavigationService.getSiteNavigation();
        const navigation = mapSiteNavigation(payload);
        reportDroppedNodes(navigation);
        set({ status: 'ready', navigation });
      } catch (error) {
        logger.error('siteNavigation.loadFailed', error, {
          feature: 'site-navigation',
          route: 'fetch',
          status: axios.isAxiosError(error) ? error.response?.status : undefined,
        });
        set({ status: 'error', navigation: null });
      }
    })();
    inFlight = request;
    // `request` never rejects (every failure is caught above); clear only our own entry.
    void request.finally(() => {
      if (inFlight === request) inFlight = null;
    });
    return request;
  },
}));
